import { accountService, taskService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { toastService } from "@miplatform/toast";
import { base64ToBlobUrl } from "@miplatform/utils";
import { useGlobalManagerApproval } from "../../../../components/GlobalManagerApprovalProvider/GlobalManagerApprovalProvider";

export const useAccountingListActionMenuViewModel = ({
  onRefresh,
  stateManager,
}) => {
  const isLoading = stateManager.isBusy();
  const { executeApiRequest } = useGlobalManagerApproval();

  const forceComplete = ({ forceCompleteReason, ...rest }) =>
    stateManager.run(async () => {
      const options = await taskService.order.getForceCompleteAccountingOptions(
        {
          ...rest,
          params: { forceCompleteReason },
        }
      );
      const response = await executeApiRequest({
        options,
        callback: onRefresh,
        noInitialErrorMessage: true,
      });

      if (response.status === HTTP_STATUSES.OK) {
        return response.data;
      }

      return null;
    });

  const getCombinedPdf = async (orderId) => {
    return stateManager.run(async () => {
      if (!orderId) {
        return null;
      }

      const response = await accountService.invoices.getCombinedPdf({
        orderId,
      });

      if (response.status === HTTP_STATUSES.OK) {
        return response.data;
      }
      return null;
    });
  };

  const handlePrint = (base64Pdf) => {
    if (!base64Pdf) {
      console.error("No base64 PDF data provided.");
      return;
    }

    try {
      const url = base64ToBlobUrl(base64Pdf, "application/pdf");

      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.left = "-9999px";
      iframe.style.top = "-9999px";
      iframe.style.width = "1px";
      iframe.style.height = "1px";
      iframe.style.opacity = "0";
      iframe.style.border = "none";
      iframe.title = "Print PDF";

      document.body.appendChild(iframe);

      iframe.src = url;

      iframe.onload = () => {
        setTimeout(() => {
          try {
            iframe.focus();

            if (iframe.contentWindow) {
              iframe.contentWindow.focus();
              iframe.contentWindow.print();
            } else {
              window.print();
            }
          } catch (err) {
            console.error("Print failed:", err);

            window.print();
          }
        }, 100);
      };

      iframe.onerror = () => {
        console.error("Failed to load PDF in iframe");

        if (document.body.contains(iframe)) {
          document.body.removeChild(iframe);
        }
        URL.revokeObjectURL(url);
      };
    } catch (error) {
      console.error("Error processing PDF:", error);
    }
  };

  const voidOrder = ({ orderId, modifiedOn }) =>
    stateManager.run(async () => {
      const options = await taskService.order.getVoidOrderOptions({
        orderId,
        modifiedOn,
      });

      const response = await executeApiRequest({
        options,
        callback: onRefresh,
        toastMessageForManagerApproval: {
          text: `Voiding an order requires manager approval.`,
          subtext: `Please provide your username and PIN to proceed.`,
        },
      });

      if (response.status === HTTP_STATUSES.OK) {
        toastService.success({ text: "Order voided successfully." });
        return response.data;
      }

      return null;
    });

  return {
    isLoading,
    voidOrder,
    forceComplete,
    getCombinedPdf,
    handlePrint,
  };
};
